import { HTTP_STATUS_CODE } from '@api/api';
import { useProjectSettings } from '@api/settings';
import {
	activateUsers,
	deleteUsers,
	suspendUsers,
	uploadUsers
} from '@api/users';
import { DataGrid } from '@components/DataGrid';
import { ApprovalDialog } from '@components/Forms';
import { Box } from '@mui/system';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { UseQueryResult } from '@tanstack/react-query';
import { getUsersColumns } from '@utils/tables';
import { AxiosError } from 'axios';
import { USERS_FIELDS } from 'constants/global';
import { useToast } from 'hooks';
import React, { useState } from 'react';
import { UsersActions } from 'types/common';
import { ToastSeverity } from 'types/toast';
import { IBatchAction, InviteFunc, IUser, IUsers } from 'types/users';
import { getIUserDisplayName } from 'utils/users';
import UserEditTenantButton from './Actions/UserEditTenantButton';
import UserInviteButton from './Actions/UserInviteButton';
import UsersBatchButton from './Actions/UsersBatchButton';
import UserUploadButton from './Actions/UserUploadButton';
import UserSingleEditDialog from './Dialogs/UserSingleEditDialog';

const editUserInitialState = {
	open: false,
	user: undefined
};

const initialActionState = {
	open: false,
	id: '',
	name: '',
	action: ''
};

type BatchAPIFunction = (data: IBatchAction) => Promise<void>;

interface IUsersTableProps extends Partial<DataGridProProps> {
	getUsers: () => UseQueryResult<IUsers>;
	adminInvite: boolean;
	uploadUsersButton: boolean;
	disableBatchActions: boolean;
	inviteFunc: InviteFunc;
	deleteFunc: BatchAPIFunction;
}

const UsersTable: React.FC<IUsersTableProps> = ({
	getUsers,
	adminInvite,
	uploadUsersButton,
	disableBatchActions,
	inviteFunc,
	deleteFunc,
	...dataGridProps
}) => {
	const { addToast } = useToast();
	const { data, isLoading, refetch } = getUsers();
	const { data: dataPS } = useProjectSettings();

	const [editSingleUser, setEditSingleOpen] = useState<{
		open: boolean;
		user?: IUser;
	}>(editUserInitialState);

	const [isUploadingUser, setUploadingUser] = useState(false);

	const handleActionSuccess = React.useCallback(async () => {
		try {
			await refetch();
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('users.fetch.failed'),
				error
			});
		}
	}, [addToast, refetch]);

	const handleUploadUsers = React.useCallback(
		async (file: Blob) => {
			try {
				setUploadingUser(true);
				await uploadUsers(file);
				await refetch();
				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr('users.upload.success')
				});
			} catch (error) {
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('users.upload.failed'),
					error
				});
			} finally {
				setUploadingUser(false);
			}
		},
		[addToast, refetch]
	);

	const [actionConfirmation, setActionConfirmation] =
		React.useState(initialActionState);

	const onDeleteActionClicked = React.useCallback(
		(user: IUser) => {
			setActionConfirmation({
				open: true,
				id: user.id,
				name: getIUserDisplayName(user),
				action: UsersActions.delete
			});
		},
		[setActionConfirmation]
	);

	const onSuspendActionClicked = React.useCallback(
		(user: IUser) => {
			setActionConfirmation({
				open: true,
				id: user.id,
				name: getIUserDisplayName(user),
				action: UsersActions.suspend
			});
		},
		[setActionConfirmation]
	);

	const onActivateActionClicked = React.useCallback(
		(user: IUser) => {
			setActionConfirmation({
				open: true,
				id: user.id,
				name: getIUserDisplayName(user),
				action: UsersActions.activate
			});
		},
		[setActionConfirmation]
	);

	const onEditSingleUserClicked = React.useCallback(
		(user: IUser) => {
			setEditSingleOpen({
				open: true,
				user
			});
		},
		[setEditSingleOpen]
	);

	const handleAction = React.useCallback(
		async (api: BatchAPIFunction, action: string, id: string) => {
			try {
				await api({ ids: [id] });
				setActionConfirmation(initialActionState);
				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr(`users.${action}.dialog.approve`)
				});
				await refetch();
			} catch (error) {
				setActionConfirmation(initialActionState);

				const err = error as AxiosError;
				if (
					err.isAxiosError &&
					err.response?.status === HTTP_STATUS_CODE.forbidden
				) {
					addToast({
						severity: ToastSeverity.Error,
						content: intl.tr('company.admins.invite.error.forbidden.message'),
						error
					});
				} else {
					addToast({
						severity: ToastSeverity.Error,
						content: intl.tr(`users.${action}.dialog.failed`),
						error
					});
				}
			}
		},
		[refetch, addToast, setActionConfirmation]
	);

	const onDeleteActionConfirmed = React.useCallback(async () => {
		await handleAction(deleteFunc, UsersActions.delete, actionConfirmation.id);
	}, [actionConfirmation.id, deleteFunc, handleAction]);

	const onSuspendActionConfirmed = React.useCallback(async () => {
		await handleAction(
			suspendUsers,
			UsersActions.suspend,
			actionConfirmation.id
		);
	}, [actionConfirmation, handleAction]);

	const onActivateActionConfirmed = React.useCallback(async () => {
		await handleAction(
			activateUsers,
			UsersActions.activate,
			actionConfirmation.id
		);
	}, [actionConfirmation, handleAction]);

	const approveFunctions: { [index: string]: () => {} } = {
		delete: onDeleteActionConfirmed,
		suspend: onSuspendActionConfirmed,
		activate: onActivateActionConfirmed
	};

	const handleCancelConfirmation = React.useCallback(() => {
		setActionConfirmation(initialActionState);
	}, [setActionConfirmation]);

	const [selectedRows, setSelectedRows] = React.useState<string[]>([]);

	const handleBatchAction = React.useCallback(
		async (api: BatchAPIFunction, action: string, ids: string[]) => {
			try {
				await api({ ids });
				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr(`users.${action}.dialog.approve`)
				});
				await refetch();
			} catch (error) {
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr(`users.${action}.dialog.failed`),
					error
				});
			}
		},
		[refetch, addToast]
	);

	const handleBatchDeleteAction = React.useCallback(async () => {
		await handleBatchAction(deleteUsers, UsersActions.delete, selectedRows);
	}, [selectedRows, handleBatchAction]);

	const handleBatchSuspendAction = React.useCallback(async () => {
		await handleBatchAction(suspendUsers, UsersActions.suspend, selectedRows);
	}, [selectedRows, handleBatchAction]);

	const handleBatchActivateAction = React.useCallback(async () => {
		await handleBatchAction(activateUsers, UsersActions.activate, selectedRows);
	}, [selectedRows, handleBatchAction]);

	const userColumns = React.useMemo(
		() =>
			getUsersColumns(
				disableBatchActions ? undefined : onEditSingleUserClicked,
				onDeleteActionClicked,
				disableBatchActions ? undefined : onSuspendActionClicked,
				disableBatchActions ? undefined : onActivateActionClicked
			),
		[
			disableBatchActions,
			onEditSingleUserClicked,
			onDeleteActionClicked,
			onSuspendActionClicked,
			onActivateActionClicked
		]
	);

	const batchActions = React.useMemo(
		() => [
			<UsersBatchButton
				key={UsersActions.delete}
				action={UsersActions.delete}
				ids={selectedRows}
				onApprove={handleBatchDeleteAction}
			/>,
			<UsersBatchButton
				key={UsersActions.suspend}
				action={UsersActions.suspend}
				ids={selectedRows}
				onApprove={handleBatchSuspendAction}
			/>,
			<UsersBatchButton
				key={UsersActions.activate}
				action={UsersActions.activate}
				ids={selectedRows}
				onApprove={handleBatchActivateAction}
			/>,
			<UserEditTenantButton
				key={UsersActions.editTenants}
				ids={selectedRows}
				onApprove={handleActionSuccess}
			/>
		],
		[
			handleActionSuccess,
			handleBatchActivateAction,
			handleBatchDeleteAction,
			handleBatchSuspendAction,
			selectedRows
		]
	);

	const globalActions = React.useMemo(
		() => (
			<>
				<UserInviteButton
					onUsersInvited={handleActionSuccess}
					adminInvite={!!adminInvite}
					inviteFunc={inviteFunc}
					inviteUrl={dataPS?.inviteURL}
				/>
				{uploadUsersButton && (
					<UserUploadButton
						onUsersUpload={handleUploadUsers}
						loading={isUploadingUser}
					/>
				)}
			</>
		),
		[
			adminInvite,
			dataPS?.inviteURL,
			handleActionSuccess,
			handleUploadUsers,
			inviteFunc,
			isUploadingUser,
			uploadUsersButton
		]
	);

	const actions =
		!disableBatchActions && selectedRows.length > 0
			? batchActions
			: globalActions;

	const approveFunc = approveFunctions[actionConfirmation.action];

	return (
		<Box display="flex" flexGrow={1} data-testid="users-table">
			{editSingleUser.open && (
				<UserSingleEditDialog
					user={editSingleUser.user!}
					onCancel={() => {
						setEditSingleOpen(editUserInitialState);
					}}
					onSaved={async () => {
						setEditSingleOpen(editUserInitialState);
						await handleActionSuccess();
					}}
				/>
			)}
			{actionConfirmation.open && (
				<ApprovalDialog
					onApprove={approveFunc}
					onCancel={handleCancelConfirmation}
					title={intl.tr(`users.${actionConfirmation.action}.dialog.title`)}
					question={intl.tr(
						`users.${actionConfirmation.action}.dialog.question`,
						{
							userId: actionConfirmation.name
						}
					)}
					approveText={intl.tr(
						`users.table.action.${actionConfirmation.action}.button`
					)}
					cancelText={intl.tr('button.cancel')}
				/>
			)}
			<DataGrid
				loading={isLoading}
				rows={data?.users || []}
				columns={userColumns}
				checkboxSelection={!disableBatchActions}
				initialState={{
					sorting: {
						sortModel: [{ field: USERS_FIELDS.createdTime, sort: 'desc' }]
					}
				}}
				onSelectionModelChange={(newSelectionModel) => {
					const ids = newSelectionModel.map((m) => m.toString());
					setSelectedRows(ids);
				}}
				noDataMessageId="grid.data.no.data.users"
				actions={actions}
				{...dataGridProps}
			/>
		</Box>
	);
};

export default UsersTable;
